import { Chart as ChartJS } from "chart.js";

const PORTAL_ANALYTICS_FONT_FAMILY = "Roboto, Helvetica, Arial, sans-serif";

export function getPortalAnalyticsDevicePixelRatio() {
  if (typeof window === "undefined") {
    return 1;
  }

  return Math.min(window.devicePixelRatio || 1, 2);
}

ChartJS.defaults.devicePixelRatio = getPortalAnalyticsDevicePixelRatio();
ChartJS.defaults.font.family = PORTAL_ANALYTICS_FONT_FAMILY;

export function portalAnalyticsFont(sizePx, weight = "700") {
  return {
    size: sizePx,
    weight,
    family: PORTAL_ANALYTICS_FONT_FAMILY,
  };
}

export function snapCanvasCoordinate(value) {
  const ratio = getPortalAnalyticsDevicePixelRatio();
  return Math.round(value * ratio) / ratio;
}

export function createPortalAnalyticsBarValueLabelsPlugin({
  offsetPx = 6,
  topInsetPx = 6,
  fontSizePx = 16,
} = {}) {
  return {
    id: "portalAnalyticsBarLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const datasetCount = chart.data.datasets.length;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        const isLeftBar = datasetIndex === 0;
        const isRightBar = datasetIndex === datasetCount - 1;

        meta.data.forEach((bar, index) => {
          const value = dataset.data[index];
          if (value == null) {
            return;
          }

          const barLeft = bar.x - bar.width / 2;
          const barRight = bar.x + bar.width / 2;
          const barTopY = Math.min(bar.y, bar.base);

          let labelX;
          let textAlign;

          if (isLeftBar) {
            labelX = barLeft - offsetPx;
            textAlign = "right";
          } else if (isRightBar) {
            labelX = barRight + offsetPx;
            textAlign = "left";
          } else {
            return;
          }

          const labelColor = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor[index]
            : dataset.backgroundColor;

          ctx.save();
          ctx.fillStyle = labelColor || "#636466";
          ctx.font = `700 ${fontSizePx}px ${PORTAL_ANALYTICS_FONT_FAMILY}`;
          ctx.textAlign = textAlign;
          ctx.textBaseline = "top";
          ctx.fillText(
            String(value),
            snapCanvasCoordinate(labelX),
            snapCanvasCoordinate(barTopY + topInsetPx),
          );
          ctx.restore();
        });
      });
    },
  };
}

export function createPortalAnalyticsPiePercentLabelsPlugin({
  minPercent = 8,
  fontSizePx = 15,
} = {}) {
  return {
    id: "portalAnalyticsPiePercentLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const dataset = chart.data.datasets[0];
      const meta = chart.getDatasetMeta(0);
      const total = dataset.data.reduce((sum, value) => sum + Number(value || 0), 0);

      if (!total) {
        return;
      }

      meta.data.forEach((arc, index) => {
        const value = Number(dataset.data[index] || 0);
        const percent = Math.round((value / total) * 100);

        if (percent < minPercent) {
          return;
        }

        const angle = (arc.startAngle + arc.endAngle) / 2;
        const radius = (arc.innerRadius + arc.outerRadius) / 2;
        const labelX = snapCanvasCoordinate(arc.x + Math.cos(angle) * radius);
        const labelY = snapCanvasCoordinate(arc.y + Math.sin(angle) * radius);

        ctx.save();
        ctx.fillStyle = "#ffffff";
        ctx.font = `700 ${fontSizePx}px ${PORTAL_ANALYTICS_FONT_FAMILY}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${percent}%`, labelX, labelY);
        ctx.restore();
      });
    },
  };
}

export function portalAnalyticsChartOptions(extra = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: getPortalAnalyticsDevicePixelRatio(),
    animation: false,
    ...extra,
  };
}
